const CAP16 = {
  id: 16,
  slug: "cap16-fundamentos-blockchain-ii",
  title: "Capítulo 16 — Fundamentos básicos de la tecnología Blockchain (II)",
  sections: [
    {
      heading: "¿Qué es un bloque?",
      text: [
        "Un bloque es la unidad básica de almacenamiento dentro de una cadena de bloques. Puedes imaginarlo como un eslabón de una cadena, donde cada bloque contiene información registrada de forma **segura, ordenada e inmutable**.",
        "Cada bloque almacena un conjunto de transacciones (operaciones realizadas en la red), junto con datos técnicos que ayudan a proteger y validar esa información."
      ]
    },

    {
      heading: "¿Qué información contiene un bloque?",
      text: [
        "Cada bloque no solo guarda transacciones, sino también **metadatos técnicos** que permiten verificar su autenticidad, orden y seguridad.",
        "Los elementos más importantes que contiene un bloque son:"
      ],
      list: [
        "🔹 **Hash del bloque:** Identificador único generado criptográficamente a partir de todo el contenido del bloque.",
        "🔹 **Hash del bloque anterior:** Vincula cada bloque con el anterior, creando la cadena (esto se llama 'encadenamiento criptográfico').",
        "🔹 **Timestamp:** Marca de tiempo que indica cuándo se creó o minó el bloque.",
        "🔹 **Nonce:** Número aleatorio utilizado durante el proceso de minería para cumplir ciertas reglas del algoritmo.",
        "🔹 **Merkle Root:** Resumen criptográfico de todas las transacciones del bloque, generado usando el árbol de Merkle.",
        "🔹 **Lista de transacciones:** Registro de las operaciones realizadas, como transferencias, contratos inteligentes o actualizaciones."
      ]
    },

    {
      heading: "¿Por qué blockchain es inmutable?",
      text: [
        "La estructura de blockchain hace que modificar la información antigua sea extremadamente difícil.",
        "Si alguien intenta cambiar un bloque, se modificaría su hash y **todos los bloques siguientes se volverían inválidos**, haciendo evidente la manipulación.",
        "Esto significa que, una vez que un bloque se confirma, su contenido **no puede ser borrado ni editado**, lo que nos da una red:",
        "🔹 Segura 🔹 Transparente 🔹 Confiable 🔹 Resistente a fraudes"
      ]
    },

    {
      heading: "El árbol de Merkle (Merkle Tree)",
      text: [
        "El árbol de Merkle es una estructura criptográfica que organiza todas las transacciones dentro de un bloque.",
        "Su función principal es resumir todas las transacciones en una sola huella digital llamada **Merkle Root**, que representa todo el contenido del bloque.",
        "Gracias al árbol de Merkle, la verificación de transacciones es rápida y eficiente, sin tener que revisar todos los registros uno por uno."
      ]
    },

    {
      heading: "Protocolos de consenso",
      text: [
        "Los protocolos de consenso son reglas que permiten que los nodos de la red lleguen a un acuerdo sobre cuáles transacciones son válidas.",
        "Son fundamentales para mantener una sola versión verdadera del libro mayor y evitar conflictos, fraudes o doble gasto.",
        "Los más conocidos son:"
      ],
      list: [
        "🔹 **Proof of Work (PoW):** Seguro y robusto, pero consume mucha energía.",
        "🔹 **Proof of Stake (PoS):** Eficiente y rápido, pero puede concentrar poder en grandes tenedores.",
        "🔹 **DPoS, PoA, dPoW, PoH, Hybrid, PoB**, entre otros, según la red y su propósito."
      ],
      note:
        "Más adelante estudiaremos cada protocolo de consenso, sus ventajas, desventajas y aplicaciones prácticas."
    },

    {
      heading: "Importancia del consenso",
      text: [
        "Gracias al consenso, todos los nodos mantienen una única versión válida de la blockchain.",
        "El consenso garantiza:",
      ],
      list: [
        "🔹 Evitar el doble gasto.",
        "🔹 Validar transacciones honestamente.",
        "🔹 Mantener la descentralización.",
        "🔹 Proteger la red frente a atacantes."
      ]
    },

    {
      heading: "Smart Contracts (contratos inteligentes)",
      text: [
        "Los Smart Contracts son programas informáticos que se ejecutan automáticamente cuando se cumplen ciertas condiciones.",
        "Funcionan como acuerdos digitales que no requieren intermediarios: todo se ejecuta de forma automática, segura e irreversible.",
        "Se utilizan para crear aplicaciones descentralizadas (DApps), intercambiar valor, gestionar identidad, logística, seguros y mucho más."
      ],
      list: [
        "🔹 Son transparentes (todos pueden ver cómo están programados).",
        "🔹 Son inmutables (una vez desplegados, no se pueden modificar).",
        "🔹 Son autónomos (se ejecutan solos sin intervención humana)."
      ],
      note:
        "En próximos capítulos profundizaremos en Smart Contracts, DApps, DAOs, oráculos y ejemplos reales."
    },

    {
      heading: "Cierre del capítulo",
      text: [
        "En este capítulo conociste cómo está estructurado realmente un bloque, cómo se encadenan entre sí y cómo los mecanismos de consenso permiten asegurar y validar la información.",
        "También viste cómo los Smart Contracts ampliaron el uso de blockchain, convirtiéndola en mucho más que una tecnología para transferir dinero: hoy permite construir aplicaciones completas, automatizadas y descentralizadas.",
        "A partir del siguiente capítulo, comenzaremos a conectar estos conceptos con casos reales, plataformas y el uso práctico de blockchain."
      ]
    }
  ]
};

export default CAP16;
